import { Space, Tooltip, Typography } from "antd";
import {
  AlertOutlined,
  BankOutlined,
  InfoCircleOutlined,
  QuestionOutlined,
} from "@ant-design/icons";
import { useMemo, useState, useCallback } from "react";
import type { ColumnType, ColumnGroupType } from "antd/es/table";
import { ResponseInventoryControlRegistry } from "api/generated";
import { useIsMobile } from "hooks/useIsMobile";
import { useSelfPagination } from "hooks/commons/usePaginationTable";
import {
  areEquals,
  differenceFormatter,
  volumeFormatter,
} from "services/utils/formatterUtil";
import DrawerDashoardDetails from "./components/DrawerDashboardDetails";
import { ResponsiveTable } from "common/components/table/ResponsiveTable";

export interface DashboardTableRecord extends ResponseInventoryControlRegistry {
  key: string;
  _entradasVol: number;
  _entradasCfdi: number;
  _inputsDiffPct: number;
  _salidasVol: number;
  _salidasCfdi: number;
  _outputsDiffPct: number;
  _invDiffPct: number;
  _hasIssues: number;
}

interface DashboardTableProps {
  data: ResponseInventoryControlRegistry[];
  filterText?: string;
}

function mismatchCell(
  calculated: number | undefined,
  control: number | undefined,
): React.ReactNode {
  const bad = !areEquals(calculated, control);
  const volumeToPrint = volumeFormatter(calculated);
  if (!bad) {
    return volumeToPrint;
  }
  return (
    <Tooltip title={`Control de Existencias: ${volumeFormatter(control)}`}>
      <Space>
        <Typography.Text>{volumeFormatter(calculated)}</Typography.Text>
        <AlertOutlined style={{ color: "blue" }} />
      </Space>
    </Tooltip>
  );
}

/** Funcion para calcular totales y errores en un registro del dashboard */
function toTableRecord(
  row: ResponseInventoryControlRegistry,
  i: number,
): DashboardTableRecord {
  const entradasVol =
    (row.calculatedTotalReceptions ?? 0) + (row.calculatedTotalTraspasos ?? 0);
  const entradasCfdi =
    (row.calculatedTotalCfdiReceptions ?? 0) +
    (row.calculatedTotalCfdiTraspasos ?? 0);
  const inputsDiff = entradasCfdi - entradasVol;
  const inputsDiffPct =
    entradasVol !== 0 ? (Math.abs(inputsDiff) / entradasVol) * 100 : 0;

  const salidasVol = row.calculatedTotalSales ?? 0;
  const salidasCfdi = row.calculatedTotalCfdiSales ?? 0;
  const outputsDiff = salidasCfdi - salidasVol;
  const outputsDiffPct =
    salidasVol !== 0 ? (Math.abs(outputsDiff) / salidasVol) * 100 : 0;

  const invDiffPct = Math.abs(row.calculatedPercentageDifference ?? 0);

  const hasIssues = row.hasInventoryInconsistencies ? 1 : 0;

  return {
    ...row,
    key: row.distributionPointId ?? String(i),
    _entradasVol: entradasVol,
    _entradasCfdi: entradasCfdi,
    _inputsDiffPct: inputsDiffPct,
    _salidasVol: salidasVol,
    _salidasCfdi: salidasCfdi,
    _outputsDiffPct: outputsDiffPct,
    _invDiffPct: invDiffPct,
    _hasIssues: hasIssues,
  };
}

export function DashboardTable({ data, filterText }: DashboardTableProps) {
  const { isMobile } = useIsMobile();
  const [selectedRecord, setSelectedRecord] = useState<DashboardTableRecord>();

  const filtered = useMemo(() => {
    if (!filterText?.trim()) return data;
    const q = filterText.trim().toLowerCase();
    return data.filter(
      (row) =>
        row.distributionPointSenerLicense?.toLowerCase().includes(q) ||
        row.distributionPointName?.toLowerCase().includes(q),
    );
  }, [data, filterText]);

  const dataSource: DashboardTableRecord[] = useMemo(
    () =>
      filtered.map(toTableRecord).sort((a, b) => b._hasIssues - a._hasIssues),
    [filtered],
  );
  const pagination = useSelfPagination(dataSource, isMobile ? 10 : 20);

  const handleOpenDrawer = useCallback((r: DashboardTableRecord) => {
    setSelectedRecord(r);
  }, []);

  type Col = ColumnType<DashboardTableRecord>;
  type ColGroup = ColumnGroupType<DashboardTableRecord>;

  const columns = useMemo((): (Col | ColGroup)[] => {
    return [
      // ── Identificación ──────────────────────────────────────────────────
      {
        title: "Identificación",
        onHeaderCell: () => ({
          style: {
            background: "#f6ffed",
            borderLeft: "2px solid #b7eb8f",
            borderRight: "2px solid #b7eb8f",
            fontWeight: 700,
            textAlign: "center" as const,
          },
        }),
        children: [
          {
            title: "Punto de Distribución",
            onHeaderCell: () => ({
              style: { borderLeft: "2px solid #b7eb8f" },
            }),
            dataIndex: "distributionPointName",
            key: "distributionPointName",
            fixed: isMobile ? undefined : "left",
            ellipsis: true,
            width: "24%",
            sorter: (a, b) =>
              (a.distributionPointName ?? "").localeCompare(
                b.distributionPointName ?? "",
              ),
            render: (name: string, record: DashboardTableRecord) => (
              <Space>
                <Typography.Text strong>{name}</Typography.Text>

                <Typography.Text type="secondary">
                  {` (${record.distributionPointSenerLicense})`}
                </Typography.Text>
              </Space>
            ),
          } as Col,
        ],
      } as ColGroup,

      // ── Entradas ────────────────────────────────────────────────────────
      {
        title: "ENTRADAS",
        onHeaderCell: () => ({
          style: {
            background: "#e6f4ff",
            borderLeft: "2px solid #91caff",
            borderRight: "2px solid #91caff",
            fontWeight: 700,
            textAlign: "center" as const,
          },
        }),
        children: [
          {
            title: "Volumen",
            onHeaderCell: () => ({
              style: { borderLeft: "2px solid #91caff" },
            }),
            key: "entradasVol",
            align: "right",
            width: "7%",
            sorter: (a, b) => a._entradasVol - b._entradasVol,
            render: (_v: unknown, record: DashboardTableRecord) =>
              volumeFormatter(record._entradasVol),
          } as Col,
          {
            title: "Volumen (CFDI)",
            onHeaderCell: () => ({
              style: { borderLeft: "2px solid #91caff" },
            }),
            key: "entradasCfdi",
            align: "right",
            width: "7%",
            sorter: (a, b) => a._entradasCfdi - b._entradasCfdi,
            render: (_v: unknown, record: DashboardTableRecord) =>
              volumeFormatter(record._entradasCfdi),
          } as Col,
          {
            title: "Diferencia",
            onHeaderCell: () => ({
              style: { borderLeft: "2px solid #91caff" },
            }),
            key: "inputsDiff",
            align: "right",
            width: "9%",
            sorter: (a, b) => a._inputsDiffPct - b._inputsDiffPct,
            render: (_v: unknown, record: DashboardTableRecord) => {
              const diff = record._entradasCfdi - record._entradasVol;
              return differenceFormatter(diff, record._inputsDiffPct);
            },
          } as Col,
        ],
      } as ColGroup,

      // ── Salidas ─────────────────────────────────────────────────────────
      {
        title: "SALIDAS",
        onHeaderCell: () => ({
          style: {
            background: "#f6ffed",
            borderLeft: "2px solid #b7eb8f",
            borderRight: "2px solid #b7eb8f",
            fontWeight: 700,
            textAlign: "center" as const,
          },
        }),
        children: [
          {
            title: "Volumen",
            onHeaderCell: () => ({
              style: { borderLeft: "2px solid #b7eb8f" },
            }),
            key: "salidasVol",
            align: "right",
            width: "7%",
            sorter: (a, b) => a._salidasVol - b._salidasVol,
            render: (_v: unknown, record: DashboardTableRecord) =>
              volumeFormatter(record._salidasVol),
          } as Col,
          {
            title: "Volumen (CFDI)",
            onHeaderCell: () => ({
              style: { borderLeft: "2px solid #b7eb8f" },
            }),
            key: "salidasCfdi",
            align: "right",
            width: "7%",
            sorter: (a, b) => a._salidasCfdi - b._salidasCfdi,
            render: (_v: unknown, record: DashboardTableRecord) =>
              volumeFormatter(record._salidasCfdi),
          } as Col,
          {
            title: "Diferencia",
            onHeaderCell: () => ({
              style: { borderLeft: "2px solid #b7eb8f" },
            }),
            key: "outputsDiff",
            align: "right",
            width: "9%",
            sorter: (a, b) => a._outputsDiffPct - b._outputsDiffPct,
            render: (_v: unknown, record: DashboardTableRecord) => {
              const diff = record._salidasCfdi - record._salidasVol;
              return differenceFormatter(diff, record._outputsDiffPct);
            },
          } as Col,
        ],
      } as ColGroup,

      // ── Inventario ──────────────────────────────────────────────────────
      {
        title: "INVENTARIO",
        onHeaderCell: () => ({
          style: {
            background: "#e6f4ff",
            borderLeft: "2px solid #91caff",
            borderRight: "2px solid #91caff",
            fontWeight: 700,
            textAlign: "center" as const,
          },
        }),
        children: [
          {
            title: "Físico",
            onHeaderCell: () => ({
              style: { borderLeft: "2px solid #b7eb8f" },
            }),
            key: "inventarioFisico",
            align: "right",
            width: "7%",
            sorter: (a, b) =>
              (a.calculatedLatestMeasurementVolume ?? 0) -
              (b.calculatedLatestMeasurementVolume ?? 0),
            render: (_v: unknown, record: DashboardTableRecord) =>
              volumeFormatter(record.calculatedLatestMeasurementVolume),
          } as Col,
          {
            title: "Calculado",
            onHeaderCell: () => ({
              style: { borderLeft: "2px solid #b7eb8f" },
            }),
            key: "inventarioCalculado",
            align: "right",
            width: "9%",
            sorter: (a, b) =>
              (a.calculatedFinalVolume ?? 0) - (b.calculatedFinalVolume ?? 0),
            render: (_v: unknown, record: DashboardTableRecord) =>
              mismatchCell(record.calculatedFinalVolume, record.finalVolume),
          } as Col,
          {
            title: "Diferencia",
            onHeaderCell: () => ({
              style: { borderLeft: "2px solid #b7eb8f" },
            }),
            key: "inventarioDiff",
            width: "11%",
            align: "right",
            sorter: (a, b) => a._invDiffPct - b._invDiffPct,
            render: (_v: unknown, record: DashboardTableRecord) =>
              differenceFormatter(
                record.calculatedVolumeDifference,
                record.calculatedPercentageDifference,
              ),
          } as Col,
        ],
      } as ColGroup,

      // ── JSON ────────────────────────────────────────────────────────────
      {
        title: "JSON",
        onHeaderCell: () => ({
          style: {
            background: "#f6ffed",
            borderLeft: "2px solid #b7eb8f",
            borderRight: "2px solid #b7eb8f",
            fontWeight: 700,
            textAlign: "center" as const,
          },
        }),
        key: "json",
        align: "center",
        width: "3%",
        sorter: (a, b) => {
          const score = (r: DashboardTableRecord) =>
            !r.dailyJsonExist ? 1 : r.dailyJsonError ? 2 : 0;
          return score(a) - score(b);
        },
        render: (_v: unknown, record: DashboardTableRecord) => {
          if (!record.dailyJsonExist)
            return (
              <Tooltip title="Sin Json">
                <QuestionOutlined />
              </Tooltip>
            );
          if (record.dailyJsonError)
            return (
              <Tooltip title="Json Con error">
                <InfoCircleOutlined style={{ color: "red" }} />
              </Tooltip>
            );
          return (
            <Tooltip title="Ok">
              <InfoCircleOutlined style={{ color: "green" }} />
            </Tooltip>
          );
        },
      } as Col,
    ];
  }, [handleOpenDrawer]);

  return (
    <>
      <ResponsiveTable<DashboardTableRecord>
        dataSource={dataSource}
        columns={columns}
        pagination={pagination}
        scroll={{
          y: "calc(100vh - 450px)",
        }}
        size="small"
        tableLayout="auto"
        rowClassName={(record) =>
          record.hasInventoryInconsistencies ? "row-with-inconsistencies" : ""
        }
        locale={{
          emptyText: "No existen registros bajo los filtros seleccionados",
        }}
        onRow={(record) => ({
          onClick: () => handleOpenDrawer(record),
          style: { cursor: "pointer" },
        })}
        bordered
        titleDataIndex="distributionPointName"
        titleIcon={<BankOutlined style={{ color: "#1677ff" }} />}
      />

      {selectedRecord && (
        <DrawerDashoardDetails
          record={selectedRecord}
          onClose={() => setSelectedRecord(undefined)}
        />
      )}
    </>
  );
}
